import React, { useState } from "react";

import { Button, Icon, Typography } from "components";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GuidancePanelProps {
  fieldLabel: string;
  description: string;
  source: string;
  whatIsItLabel?: string;
  sourceLabel?: string;
  className?: string;
  unit?: string;
}

// ─── Collapsible section ───────────────────────────────────────────────────────

interface CollapsibleSectionProps {
  heading: string;
  content: string;
  defaultOpen: boolean;
}

const CollapsibleSection = ({ heading, content, defaultOpen }: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  if (!content) return null;
  return (
    <div className="guidance-panel-accordion-item">
      <Button className="guidance-panel-accordion-toggle" onClick={() => setIsOpen((open) => !open)}>
        <span className="guidance-panel-accordion-label">{heading}</span>
        <Icon iconName="tabler/chevron-down" size="s" className={`guidance-panel-chevron ${isOpen ? "is-open" : ""}`} />
      </Button>
      <div className={`guidance-panel-accordion-content ${isOpen ? "is-open" : ""}`}>
        {isOpen && (
          <Typography key={`${heading}-${content}`} variant="paragraph-s" color="grey-600" className="guidance-panel-content-text">
            {content}
          </Typography>
        )}
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const GuidancePanel = ({
  fieldLabel,
  description,
  source,
  whatIsItLabel = "What is this?",
  sourceLabel = "Source",
  className,
  unit,
}: GuidancePanelProps) => (
  <div className={`guidance-panel-compact${className ? ` ${className}` : ""}`}>
    <div className="guidance-panel-compact-header">
      <Typography key={`${fieldLabel}-${source}`} variant="paragraph-s" color="grey-600" className="guidance-panel-compact-field-name">
        {fieldLabel}
      </Typography>
      {unit && (
        <Typography key={`unit-${fieldLabel}`} variant="paragraph-s" color="grey-600" className="guidance-panel-compact-field-unit">
          {unit}
        </Typography>
      )}
    </div>
    <div className="guidance-panel-compact-accordion">
      <CollapsibleSection heading={whatIsItLabel} content={description} defaultOpen />
      <CollapsibleSection heading={sourceLabel} content={source} defaultOpen={false} />
    </div>
  </div>
);
